const GOOGLE_GEOCODE_ENDPOINT = 'https://maps.googleapis.com/maps/api/geocode/json';
const NOMINATIM_ENDPOINT = 'https://nominatim.openstreetmap.org/reverse';

const fetchJson = async (url, options = {}) => {
  const response = await fetch(url, {
    headers: {
      'Accept': 'application/json',
      ...options.headers,
    },
    ...options,
  });

  if (!response.ok) {
    const message = `Reverse geocoding request failed with status ${response.status}`;
    throw new Error(message);
  }

  return await response.json();
};

const tryGoogleReverseGeocode = async (latitude, longitude, apiKey, signal) => {
  if (!apiKey) {
    return null;
  }

  const params = new URLSearchParams({
    latlng: `${latitude},${longitude}`,
    key: apiKey,
  });

  if (typeof window !== 'undefined' && window.navigator?.language) {
    params.append('language', window.navigator.language);
  }

  const data = await fetchJson(`${GOOGLE_GEOCODE_ENDPOINT}?${params.toString()}`, { signal });

  if (data.status !== 'OK' || !Array.isArray(data.results) || data.results.length === 0) {
    return null;
  }

  return data.results[0]?.formatted_address || null;
};

const tryNominatimReverseGeocode = async (latitude, longitude, signal) => {
  const params = new URLSearchParams({
    format: 'jsonv2',
    lat: latitude,
    lon: longitude,
    zoom: '16',
    addressdetails: '1',
  });

  const data = await fetchJson(`${NOMINATIM_ENDPOINT}?${params.toString()}`, {
    signal,
    headers: {
      'Accept-Language': typeof window !== 'undefined' && window.navigator?.language
        ? window.navigator.language
        : 'en',
      'User-Agent': 'clinic-user-app/1.0 (+support@clinic-app.local)',
    },
  });

  return data.display_name || null;
};

export const reverseGeocode = async (latitude, longitude, { signal } = {}) => {
  if (typeof latitude !== 'number' || Number.isNaN(latitude) ||
      typeof longitude !== 'number' || Number.isNaN(longitude)) {
    throw new Error('Valid latitude and longitude are required for reverse geocoding.');
  }

  const abortController = signal ? null : new AbortController();
  const effectiveSignal = signal || abortController?.signal;

  try {
    const googleApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || process.env.GOOGLE_MAPS_API_KEY;

    const googleAddress = await tryGoogleReverseGeocode(latitude, longitude, googleApiKey, effectiveSignal);
    if (googleAddress) {
      return googleAddress;
    }

    const nominatimAddress = await tryNominatimReverseGeocode(latitude, longitude, effectiveSignal);
    if (nominatimAddress) {
      return nominatimAddress;
    }

    throw new Error('Unable to determine location address from coordinates.');
  } finally {
    abortController?.abort();
  }
};


